import React, { useContext, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Bounce, toast } from 'react-toastify';
import { BASE_URL_API } from '../../References/URLs';
import AuthContext from '../../store/auth-context';
import { useActiveStore } from '../../store/ActiveStoreContext';
import styles from './ExpenseSetting.module.css';
import {
    FlaskConical, Users, Home, Wifi, Wrench, BarChart2, ShoppingBag,
    CreditCard, Shield, Megaphone, BookOpen, Coins, Package, Truck,
    Lightbulb, Settings, TrendingUp, DollarSign, FileText, Globe,
    Building, Clipboard, Star, Tag, ChevronDown, ChevronRight,
    Zap, Scissors, Briefcase, HeartHandshake, Award,
    CheckSquare2, Square, Minus,
    Building2, Sparkles, Heart, Monitor,
    FileCheck2, ShieldCheck, Hammer, PackageX, Landmark, Car, MoreHorizontal,
    Pencil, Check, X, Menu as MenuIcon
} from 'lucide-react';

const LUCIDE_ICON_MAP = {
    FlaskConical, Users, Home, Wifi, Wrench, BarChart2, ShoppingBag,
    CreditCard, Shield, Megaphone, BookOpen, Coins, Package, Truck,
    Lightbulb, Settings, TrendingUp, DollarSign, FileText, Globe,
    Building, Clipboard, Star, Tag, Zap, Scissors, Briefcase,
    HeartHandshake, Award, Building2, Sparkles, Heart, Monitor, FileCheck2,
    ShieldCheck, Hammer, PackageX, Landmark, Car, MoreHorizontal,
    CheckSquare2, Square, Minus,
};

const CategoryIcon = ({ slug, size = 14, color = '#6b7280' }) => {
    const Icon = LUCIDE_ICON_MAP[slug];
    if (!Icon) return <span style={{ width: size, height: size, display: 'inline-block' }} />;
    return <Icon size={size} color={color} strokeWidth={1.8} />;
};

const ExpenseSetting = () => {
    const authCtx = useContext(AuthContext);
    const { activeStore } = useActiveStore();
    const storeIdentifier = activeStore ? JSON.parse(activeStore)?.storeIdentifier : null;

    // --- State ---
    const [activeTab, setActiveTab] = useState('categories');
    // Mobile only: same pattern as the Special Moments / Communication /
    // Microsite section shells — a small top-right hamburger opens a
    // left-edge drawer holding this page's own section nav, instead of a
    // horizontal tab bar eating vertical space. Desktop keeps the tab bar.
    const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
    const [standardCategoryData, setStandardCategoryData] = useState([]);
    const [customCategories, setCustomCategories] = useState([]);  // array of category objects
    const [storeData, setStoreData] = useState(null); // full response object (has totalCategories, totalSubcategories)

    // Rich add-form state
    const EMPTY_ADD_FORM = {
        target: 'category',          // 'category' | 'subcategory'
        // --- category fields ---
        categoryLabel: '',
        expenseType: 'opex',         // 'opex' | 'capex' | 'mixed'
        functionalGroup: 'other',    // 'service'|'people'|'infra'|'compliance'|'other'
        colorHex: '#888780',
        iconSlug: 'Tag',
        sortOrder: '',
        // --- subcategory fields ---
        parentCategoryId: '',        // storeCategoryIdentifier of parent
        subcategoryLabel: '',
        isGstItcEligible: 0,         // 0 | 1
        expenseTypeOverride: null,   // null | 'opex'|'capex'|'mixed'
    };
    const [isAdding, setIsAdding] = useState(false);
    const [addForm, setAddForm] = useState(EMPTY_ADD_FORM);
    const [addLoading, setAddLoading] = useState(false);
    const [addErrors, setAddErrors] = useState({});  // field → error string

    const [renameDraft, setRenameDraft] = useState({
        target: null, // 'category' | 'subcategory' | null
        storeCategoryIdentifier: '',
        storeSubcategoryIdentifier: '',
        value: ''
    });
    const [renameLoading, setRenameLoading] = useState(false);

    const [searchQuery, setSearchQuery] = useState('');
    const [filterActive, setFilterActive] = useState('all'); // all | active | inactive
    const [expandedCustom, setExpandedCustom] = useState({}); // { [storeCategoryIdentifier]: bool }
    const [expandedStandard, setExpandedStandard] = useState({}); // { [standardCategoryIdentifier]: bool }
    const [stdSearchQuery, setStdSearchQuery] = useState('');
    const [selectedCats, setSelectedCats] = useState(new Set()); // Set<standardCategoryIdentifier>
    const [selectedSubcats, setSelectedSubcats] = useState(new Set()); // Set<standardSubcategoryIdentifier>
    const [importLoading, setImportLoading] = useState(false);
    const [toggleLoading, setToggleLoading] = useState(new Set()); // Set of identifiers currently being toggled

    // --- API Calls ---
    const fetchStandardExpensesCategoryData = async () => {
        try {
            const response = await fetch(BASE_URL_API + "fetchStandardExpensesCategoryData", {
                method: "POST",
                body: JSON.stringify({ token: authCtx.token })
            });
            const respo = await response.json();
            if (respo.status === "success") {
                setStandardCategoryData(respo.response);
                // console.log("Standard Categories (for reference)", respo.response);
            } else if (respo.message === "tokenExpired") {
                authCtx.logout();
            }
        } catch (e) {
            console.error("fetchStandardExpensesCategoryData error", e);
        }
    };

    const fetchExpensesCategoryData = async () => {
        try {
            const response = await fetch(BASE_URL_API + "fetchStoreExpensesCategoryData", {
                method: "POST",
                body: JSON.stringify({ token: authCtx.token, storeIdentifier: storeIdentifier, includeInactive: true })
            });
            const respo = await response.json();
            if (respo.status === "success") {
                setStoreData(respo.response);
                setCustomCategories(Array.isArray(respo.response?.categories) ? respo.response.categories : []);
            } else if (respo.message === "tokenExpired") {
                authCtx.logout();
            }
        } catch (e) {
            console.error("fetchExpensesCategoryData error", e);
        }
    };

    const setAddField = (field, value) => {
        setAddForm(prev => ({ ...prev, [field]: value }));
        // Clear that field's error as the user types
        setAddErrors(prev => { const next = { ...prev }; delete next[field]; return next; });
    };

    const validateAddForm = () => {
        const errs = {};
        if (addForm.target === 'category') {
            if (!addForm.categoryLabel.trim()) errs.categoryLabel = 'Category name is required';
            else if (addForm.categoryLabel.trim().length > 80) errs.categoryLabel = 'Max 80 characters';
            // Check for duplicate label among existing custom categories
            const dup = customCategories.find(
                c => c.categoryLabel?.toLowerCase() === addForm.categoryLabel.trim().toLowerCase()
            );
            if (dup) errs.categoryLabel = 'A category with this name already exists';
            if (!addForm.expenseType) errs.expenseType = 'Required';
            if (!addForm.functionalGroup) errs.functionalGroup = 'Required';
            if (addForm.colorHex && !/^#[0-9a-fA-F]{6}$/.test(addForm.colorHex)) errs.colorHex = 'Invalid hex colour';
            if (addForm.sortOrder !== '' && (isNaN(Number(addForm.sortOrder)) || Number(addForm.sortOrder) < 0))
                errs.sortOrder = 'Must be a positive number';
        } else {
            if (!addForm.parentCategoryId) errs.parentCategoryId = 'Parent category is required';
            if (!addForm.subcategoryLabel.trim()) errs.subcategoryLabel = 'Subcategory name is required';
            else if (addForm.subcategoryLabel.trim().length > 120) errs.subcategoryLabel = 'Max 120 characters';
            // Check duplicate subcategory label within the selected parent
            const parent = customCategories.find(c => c.storeCategoryIdentifier === addForm.parentCategoryId);
            if (parent) {
                const dupSub = (parent.subcategories || []).find(
                    s => s.subcategoryLabel?.toLowerCase() === addForm.subcategoryLabel.trim().toLowerCase()
                );
                if (dupSub) errs.subcategoryLabel = 'This subcategory already exists under the selected category';
            }
            if (addForm.sortOrder !== '' && (isNaN(Number(addForm.sortOrder)) || Number(addForm.sortOrder) < 0))
                errs.sortOrder = 'Must be a positive number';
        }
        setAddErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleAddCategory = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (!validateAddForm()) return;
        if (!storeIdentifier) {
            toast.error('No active store selected.', { position: 'top-center', autoClose: 3000, hideProgressBar: true });
            return;
        }
        setAddLoading(true);
        try {
            let body;
            if (addForm.target === 'category') {
                body = {
                    token: authCtx.token,
                    storeIdentifier,
                    target: 'category',
                    categoryLabel: addForm.categoryLabel.trim(),
                    expenseType: addForm.expenseType,
                    functionalGroup: addForm.functionalGroup,
                    colorHex: addForm.colorHex || '#888780',
                    iconSlug: addForm.iconSlug || 'Tag',
                    ...(addForm.sortOrder !== '' && { sortOrder: Number(addForm.sortOrder) }),
                };
            } else {
                body = {
                    token: authCtx.token,
                    storeIdentifier,
                    target: 'subcategory',
                    storeCategoryIdentifier: addForm.parentCategoryId,
                    subcategoryLabel: addForm.subcategoryLabel.trim(),
                    isGstItcEligible: addForm.isGstItcEligible,
                    expenseTypeOverride: addForm.expenseTypeOverride || null,
                    ...(addForm.sortOrder !== '' && { sortOrder: Number(addForm.sortOrder) }),
                };
            }
            const response = await fetch(BASE_URL_API + 'addCustomExpensesCategoryData', {
                method: 'POST',
                body: JSON.stringify(body)
            });
            const respo = await response.json();
            if (respo.status === 'success') {
                const r = respo.response;
                const msg = addForm.target === 'category'
                    ? `Category "${r.categoryLabel}" created${
                        r.subcategoriesCreated > 0 ? ` with ${r.subcategoriesCreated} subcategor${r.subcategoriesCreated === 1 ? 'y' : 'ies'}` : ''
                      }`
                    : `Subcategory "${r.subcategoryLabel}" added`;
                toast.success(msg, { position: 'top-center', autoClose: 2500, hideProgressBar: true });
                setAddForm(EMPTY_ADD_FORM);
                setAddErrors({});
                setIsAdding(false);
                fetchExpensesCategoryData();
            } else if (respo.message === 'tokenExpired') {
                authCtx.logout();
            } else {
                // Surface API validation/duplicate errors against the right field
                const msg = respo.message || 'Failed to create';
                if (msg.toLowerCase().includes('label') || msg.toLowerCase().includes('category with this')) {
                    setAddErrors(prev => ({
                        ...prev,
                        [addForm.target === 'category' ? 'categoryLabel' : 'subcategoryLabel']: msg
                    }));
                } else {
                    toast.error(msg, { position: 'top-center', autoClose: 3500, hideProgressBar: true });
                }
            }
        } catch (e) {
            toast.error('Server error. Please try again.', { position: 'top-center', autoClose: 3000, hideProgressBar: true });
        }
        setAddLoading(false);
    };

    const handleToggleCategory = async (cat) => {
        const id = cat.storeCategoryIdentifier;
        const nextActive = cat.isActive === '1' || cat.isActive === 1 ? 0 : 1;
        setToggleLoading(prev => new Set(prev).add(id));
        try {
            const response = await fetch(BASE_URL_API + "toggleStoreExpenseCategory", {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier,
                    target: "category",
                    storeCategoryIdentifier: id,
                    isActive: nextActive
                })
            });
            const respo = await response.json();
            if (respo.status === "success") {
                const action = nextActive === 1 ? 'activated' : 'deactivated';
                const subcatCount = respo.response?.subcategoriesAffected;
                toast.success(
                    subcatCount > 0
                        ? `Category ${action} · ${subcatCount} subcategor${subcatCount === 1 ? 'y' : 'ies'} updated`
                        : `Category ${action}`,
                    { position: "top-center", autoClose: 2500, hideProgressBar: true }
                );
                fetchExpensesCategoryData();
            } else if (respo.message === "tokenExpired") {
                authCtx.logout();
            } else {
                toast.error(respo.message || 'Toggle failed', { position: "top-center", autoClose: 3000, hideProgressBar: true });
            }
        } catch (e) {
            toast.error('Server error', { position: "top-center", autoClose: 3000, hideProgressBar: true });
        }
        setToggleLoading(prev => { const next = new Set(prev); next.delete(id); return next; });
    };

    const handleToggleSubcat = async (cat, sub) => {
        const id = sub.storeSubcategoryIdentifier;
        const nextActive = sub.isActive === '1' || sub.isActive === 1 ? 0 : 1;
        setToggleLoading(prev => new Set(prev).add(id));
        try {
            const response = await fetch(BASE_URL_API + "toggleStoreExpenseCategory", {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier,
                    target: "subcategory",
                    storeCategoryIdentifier: cat.storeCategoryIdentifier,
                    storeSubcategoryIdentifier: id,
                    isActive: nextActive
                })
            });
            const respo = await response.json();
            if (respo.status === "success") {
                toast.success(
                    nextActive === 1 ? 'Subcategory activated' : 'Subcategory deactivated',
                    { position: "top-center", autoClose: 2000, hideProgressBar: true }
                );
                fetchExpensesCategoryData();
            } else if (respo.message === "tokenExpired") {
                authCtx.logout();
            } else {
                toast.error(respo.message || 'Toggle failed', { position: "top-center", autoClose: 3000, hideProgressBar: true });
            }
        } catch (e) {
            toast.error('Server error', { position: "top-center", autoClose: 3000, hideProgressBar: true });
        }
        setToggleLoading(prev => { const next = new Set(prev); next.delete(id); return next; });
    };

    const startRenameCategory = (cat) => {
        setRenameDraft({
            target: 'category',
            storeCategoryIdentifier: cat.storeCategoryIdentifier,
            storeSubcategoryIdentifier: '',
            value: cat.categoryLabel ?? ''
        });
    };

    const startRenameSubcategory = (cat, sub) => {
        setRenameDraft({
            target: 'subcategory',
            storeCategoryIdentifier: cat.storeCategoryIdentifier,
            storeSubcategoryIdentifier: sub.storeSubcategoryIdentifier,
            value: sub.subcategoryLabel ?? ''
        });
    };

    const cancelRename = () => {
        setRenameDraft({ target: null, storeCategoryIdentifier: '', storeSubcategoryIdentifier: '', value: '' });
    };

    const handleRenameSave = async () => {
        const newLabel = renameDraft.value.trim();
        if (!newLabel) {
            toast.error('Label is required.', { position: "top-center", autoClose: 2500, hideProgressBar: true });
            return;
        }
        if (!storeIdentifier) {
            toast.error('No active store found. Please select a store and try again.', { position: "top-center", autoClose: 3000, hideProgressBar: true });
            return;
        }

        if (renameDraft.target === 'category') {
            const isDuplicate = customCategories.some(cat =>
                cat.storeCategoryIdentifier !== renameDraft.storeCategoryIdentifier
                && (cat.categoryLabel ?? '').toLowerCase() === newLabel.toLowerCase()
            );
            if (isDuplicate) {
                toast.error('A category with this label already exists for this store', { position: "top-center", autoClose: 3000, hideProgressBar: true });
                return;
            }
        }

        if (renameDraft.target === 'subcategory') {
            const parent = customCategories.find(cat => cat.storeCategoryIdentifier === renameDraft.storeCategoryIdentifier);
            const isDuplicate = (parent?.subcategories || []).some(sub =>
                sub.storeSubcategoryIdentifier !== renameDraft.storeSubcategoryIdentifier
                && (sub.subcategoryLabel ?? '').toLowerCase() === newLabel.toLowerCase()
            );
            if (isDuplicate) {
                toast.error('A subcategory with this label already exists under this category', { position: "top-center", autoClose: 3000, hideProgressBar: true });
                return;
            }
        }

        setRenameLoading(true);
        try {
            const body = {
                token: authCtx.token,
                storeIdentifier,
                target: renameDraft.target,
                storeCategoryIdentifier: renameDraft.storeCategoryIdentifier,
                newLabel
            };
            if (renameDraft.target === 'subcategory') {
                body.storeSubcategoryIdentifier = renameDraft.storeSubcategoryIdentifier;
            }

            const response = await fetch(BASE_URL_API + "updateStoreExpenseCategorySubcategoryLabel", {
                method: "POST",
                body: JSON.stringify(body)
            });

            const respo = await response.json();
            if (respo.status === "success") {
                toast.success(
                    renameDraft.target === 'category' ? 'Category renamed successfully' : 'Subcategory renamed successfully',
                    { position: "top-center", autoClose: 2200, hideProgressBar: true }
                );
                cancelRename();
                fetchExpensesCategoryData();
            } else if (respo.message === "tokenExpired") {
                authCtx.logout();
            } else {
                toast.error(respo.message || 'Rename failed', { position: "top-center", autoClose: 3200, hideProgressBar: true });
            }
        } catch (e) {
            toast.error('Server error. Please try again.', { position: "top-center", autoClose: 3000, hideProgressBar: true });
        }
        setRenameLoading(false);
    };

    const handleDeleteCategory = async (cat) => {
        if (!window.confirm(`Delete "${cat.categoryName}"? This cannot be undone.`)) return;
        try {
            const response = await fetch(BASE_URL_API + "deleteExpensesCategoryData", {
                method: "POST",
                body: JSON.stringify({ token: authCtx.token, categoryId: cat.categoryId })
            });
            const respo = await response.json();
            if (respo.status === "success") {
                toast.success('Category deleted', { position: "top-center", autoClose: 2000, hideProgressBar: true });
                fetchExpensesCategoryData();
            } else if (respo.message === "tokenExpired") {
                authCtx.logout();
            }
        } catch (e) {
            toast.error('Server error', { position: "top-center", autoClose: 3000, hideProgressBar: true });
        }
    };

    const handleImportSelected = async () => {
        if (selectedCats.size === 0) {
            toast.error('Select at least one category to import.', { position: "top-center", autoClose: 2500, hideProgressBar: true });
            return;
        }
        if (!storeIdentifier) {
            toast.error('No active store found. Please select a store and try again.', { position: "top-center", autoClose: 3000, hideProgressBar: true });
            return;
        }
        setImportLoading(true);

        // Determine which mode to use:
        //  Mode 2 — category selected, all its subcats selected (or it has none)
        //  Mode 3 — category selected, only some subcats cherry-picked
        const categories = [...selectedCats].map(catId => {
            const cat = standardCategoryData.find(c => c.standardCategoryIdentifier === catId);
            if (!cat) return null;
            const allSubcats = Array.isArray(cat.subcategories) ? cat.subcategories : [];
            const allSubIds = allSubcats.map(s => s.standardSubcategoryIdentifier);
            const pickedSubIds = allSubIds.filter(id => selectedSubcats.has(id));

            // If no subcats exist, or every subcat is selected → adoptAllSubcategories: true
            if (allSubcats.length === 0 || pickedSubIds.length === allSubIds.length) {
                return {
                    standardCategoryIdentifier: catId,
                    adoptAllSubcategories: true
                };
            }
            // Otherwise cherry-pick
            return {
                standardCategoryIdentifier: catId,
                adoptAllSubcategories: false,
                subcategories: pickedSubIds
            };
        }).filter(Boolean);

        const body = {
            token: authCtx.token,
            storeIdentifier,
            adoptAll: false,
            categories
        };

        try {
            const response = await fetch(BASE_URL_API + "importStandardExpensesCategory", {
                method: "POST",
                body: JSON.stringify(body)
            });
            const respo = await response.json();
            if (respo.status === "success") {
                toast.success(
                    `${selectedCats.size} categor${selectedCats.size === 1 ? 'y' : 'ies'} imported successfully!`,
                    { position: "top-center", autoClose: 2500, hideProgressBar: true }
                );
                setSelectedCats(new Set());
                setSelectedSubcats(new Set());
                fetchExpensesCategoryData();
            } else if (respo.message === "tokenExpired") {
                authCtx.logout();
            } else {
                if(respo.status === "partial") {
                    fetchExpensesCategoryData();
                    setSelectedCats(new Set());
                    setSelectedSubcats(new Set());
                }
                toast.error(respo.message || 'Import failed', { position: "top-center", autoClose: 3000, hideProgressBar: true });
            }
        } catch (e) {
            toast.error('Server error', { position: "top-center", autoClose: 3000, hideProgressBar: true });
        }
        setImportLoading(false);
    };

    const toggleCat = (catId, subcats) => {
        setSelectedCats(prev => {
            const next = new Set(prev);
            if (next.has(catId)) {
                // Deselect category — also deselect all its subcats
                next.delete(catId);
                setSelectedSubcats(prevSub => {
                    const nextSub = new Set(prevSub);
                    subcats.forEach(s => nextSub.delete(s.standardSubcategoryIdentifier));
                    return nextSub;
                });
            } else {
                // Select category — also select all its subcats
                next.add(catId);
                setSelectedSubcats(prevSub => {
                    const nextSub = new Set(prevSub);
                    subcats.forEach(s => nextSub.add(s.standardSubcategoryIdentifier));
                    return nextSub;
                });
            }
            return next;
        });
    };

    const toggleSubcat = (catId, subcatId, allSubcats) => {
        setSelectedSubcats(prev => {
            const next = new Set(prev);
            if (next.has(subcatId)) {
                next.delete(subcatId);
                // If no subcats of this category remain selected, also uncheck the category
                const remainingSelected = allSubcats.filter(s => s.standardSubcategoryIdentifier !== subcatId && next.has(s.standardSubcategoryIdentifier));
                if (remainingSelected.length === 0) {
                    setSelectedCats(prevCats => { const nc = new Set(prevCats); nc.delete(catId); return nc; });
                }
            } else {
                next.add(subcatId);
                // Auto-check the parent category too
                setSelectedCats(prevCats => { const nc = new Set(prevCats); nc.add(catId); return nc; });
            }
            return next;
        });
    };

    // --- Derived ---
    const filteredCustom = customCategories.filter(cat => {
        const label = cat.categoryLabel ?? '';
        const matchSearch = searchQuery.trim()
            ? label.toLowerCase().includes(searchQuery.toLowerCase()) ||
              (cat.subcategories || []).some(s => s.subcategoryLabel?.toLowerCase().includes(searchQuery.toLowerCase()))
            : true;
        return matchSearch;
    });

    // Build a map: standardCategoryIdentifier → Set of already-imported standardSubcategoryIdentifiers
    const importedSubsByStdCatId = {};
    customCategories.forEach(c => {
        if (!c.standardCategoryIdentifier) return;
        if (!importedSubsByStdCatId[c.standardCategoryIdentifier]) {
            importedSubsByStdCatId[c.standardCategoryIdentifier] = new Set();
        }
        (c.subcategories || []).forEach(s => {
            if (s.standardSubcategoryIdentifier) {
                importedSubsByStdCatId[c.standardCategoryIdentifier].add(s.standardSubcategoryIdentifier);
            }
        });
    });

    const notImportedStandard = [...standardCategoryData]
        .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder))
        .reduce((acc, cat) => {
            const importedSubs = importedSubsByStdCatId[cat.standardCategoryIdentifier];
            // Category was never imported at all — show it fully
            if (!importedSubs) {
                acc.push(cat);
                return acc;
            }
            // Category was imported — check if any subcats remain un-imported
            const allSubs = Array.isArray(cat.subcategories) ? cat.subcategories : [];
            const remainingSubs = allSubs.filter(
                s => !importedSubs.has(s.standardSubcategoryIdentifier)
            );
            if (allSubs.length === 0) {
                // No subcategories — category itself was fully adopted, hide it
                return acc;
            }
            if (remainingSubs.length === 0) {
                // All subcategories imported — hide the whole category
                return acc;
            }
            // Partially imported — show the category with only the remaining subcats
            acc.push({ ...cat, subcategories: remainingSubs });
            return acc;
        }, []);

    const filteredStandard = stdSearchQuery.trim()
        ? notImportedStandard.filter(cat =>
            cat.standardCategoryLabel?.toLowerCase().includes(stdSearchQuery.toLowerCase()) ||
            cat.standardCategoryDescription?.toLowerCase().includes(stdSearchQuery.toLowerCase()) ||
            (cat.subcategories || []).some(s => s.standardSubcategoryLabel?.toLowerCase().includes(stdSearchQuery.toLowerCase()))
        )
        : notImportedStandard;

    useEffect(() => {
        fetchStandardExpensesCategoryData();
        fetchExpensesCategoryData();
    }, []);

    // --- Tabs config (future tabs plug in here) ---
    const tabs = [
        { key: 'categories', label: 'Categories' },
        { key: 'paymentMethods', label: 'Payment Methods', disabled: false },
        { key: 'budgets', label: 'Budgets', disabled: true },
        { key: 'gst', label: 'GST / Tax', disabled: true },
    ];

    return (
        <div className={styles.mainWrapper}>
            <div className={styles.mainContainer}>

                {/* Page Header + Tabs — wrapped so mobile can give this whole
                    strip a solid white background matching the sticky panel
                    heads below, instead of the page's gradient showing through
                    behind it (desktop is untouched, this div is unstyled there). */}
                <div className={styles.mobileTopBar}>
                    <div className={styles.pageHeader}>
                        <div className={styles.pageHeaderLeft}>
                            <span className={styles.pageTitle}>Expense Settings</span>
                            <span className={styles.pageCrumb}>Settings &rsaquo; Expenses</span>
                        </div>
                        {/* Mobile only — desktop hides this and shows the tab bar below instead */}
                        <button
                            type="button"
                            className={styles.mobileNavMenuBtn}
                            onClick={() => setIsMobileNavOpen(true)}
                            aria-label="Open settings sections"
                        >
                            <MenuIcon size={18} />
                        </button>
                    </div>

                    {/* Tabs — desktop only; mobile uses the hamburger + drawer above/below */}
                    <div className={styles.tabBar}>
                        {tabs.map(tab => (
                            <button
                                key={tab.key}
                                className={`${styles.tabBtn} ${activeTab === tab.key ? styles.tabBtnActive : ''} ${tab.disabled ? styles.tabBtnDisabled : ''}`}
                                onClick={() => !tab.disabled && setActiveTab(tab.key)}
                            >
                                {tab.label}
                                {tab.disabled && <span className={styles.soonBadge}>soon</span>}
                            </button>
                        ))}
                    </div>
                </div>

                {isMobileNavOpen && (
                    <>
                        <button
                            type="button"
                            className={styles.mobileNavOverlay}
                            onClick={() => setIsMobileNavOpen(false)}
                            aria-label="Close settings sections"
                        />
                        <aside className={styles.mobileNavDrawer} aria-label="Expense settings sections">
                            <div className={styles.mobileNavDrawerHeader}>
                                <span className={styles.mobileNavDrawerTitle}>Expense Settings</span>
                                <button
                                    type="button"
                                    className={styles.mobileNavCloseBtn}
                                    onClick={() => setIsMobileNavOpen(false)}
                                    aria-label="Close"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                            <nav className={styles.mobileNavList}>
                                {tabs.map(tab => (
                                    <button
                                        key={tab.key}
                                        type="button"
                                        className={`${styles.mobileNavItem} ${activeTab === tab.key ? styles.mobileNavItemActive : ''} ${tab.disabled ? styles.mobileNavItemDisabled : ''}`}
                                        onClick={() => {
                                            if (tab.disabled) return;
                                            setActiveTab(tab.key);
                                            setIsMobileNavOpen(false);
                                        }}
                                        disabled={tab.disabled}
                                    >
                                        {tab.label}
                                        {tab.disabled && <span className={styles.soonBadge}>soon</span>}
                                    </button>
                                ))}
                            </nav>
                        </aside>
                    </>
                )}

                {/* Categories Tab */}
                {activeTab === 'categories' && (
                    <div className={styles.tabContent}>

                        {/* Two-column layout */}
                        <div className={styles.twoCol}>

                            {/* LEFT: Custom Categories */}
                            <div className={styles.panel}>
                                {/* Sticks to the top of the scroll while this panel's cards scroll
                                    beneath it, then scrolls away with the panel once its last card
                                    passes — handoff to the next panel's own sticky head is automatic,
                                    no JS needed, since each is bounded by its own .panel box. */}
                                <div className={styles.stickyPanelHead}>
                                    <div className={styles.panelHeader}>
                                        <div className={styles.panelTitleStack}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <span className={styles.panelTitle}>Your Categories</span>
                                                <span className={styles.panelCount}>{storeData?.activeCategories ?? customCategories.length}</span>
                                            </div>
                                            {storeData && (
                                                <span className={styles.panelSubtitle}>
                                                    {storeData.activeCategories} of {storeData.totalCategories} active
                                                    &nbsp;&middot;&nbsp;
                                                    {storeData.activeSubcategories} of {storeData.totalSubcategories} subcategories active
                                                </span>
                                            )}
                                        </div>
                                        {/* Desktop-only — mobile shows its own copy below, next to
                                            Search, instead of on this row (kept as two separate
                                            elements rather than relocating one in the DOM, so neither
                                            layout has to compromise for the other). */}
                                        <button
                                            className={styles.addBtn}
                                            onClick={() => { setAddForm(EMPTY_ADD_FORM); setAddErrors({}); setIsAdding(true); }}
                                        >
                                            + Add
                                        </button>
                                    </div>

                                    {/* Search + Add share one row on mobile */}
                                    <div className={styles.filterRow}>
                                        <input
                                            type="text"
                                            placeholder="Search categories..."
                                            value={searchQuery}
                                            onChange={e => setSearchQuery(e.target.value)}
                                            className={styles.searchInput}
                                        />
                                        <button
                                            className={styles.addBtnMobile}
                                            onClick={() => { setAddForm(EMPTY_ADD_FORM); setAddErrors({}); setIsAdding(true); }}
                                        >
                                            + Add
                                        </button>
                                    </div>
                                </div>

                                {/* Category List */}
                                <div className={styles.categoryList}>
                                    {filteredCustom.length === 0 && (
                                        <div className={styles.emptyState}>
                                            {searchQuery ? 'No categories match your search.' : 'No categories yet. Import from standards or add a custom one.'}
                                        </div>
                                    )}
                                    {filteredCustom.map(cat => {
                                        const subcats = Array.isArray(cat.subcategories) ? cat.subcategories : [];
                                        const isExp = !!expandedCustom[cat.storeCategoryIdentifier];
                                        const isCatActive = cat.isActive === '1' || cat.isActive === 1;
                                        const isCatToggling = toggleLoading.has(cat.storeCategoryIdentifier);
                                        return (
                                            <div
                                                key={cat.storeCategoryIdentifier}
                                                className={`${styles.cusCard} ${!isCatActive ? styles.cusCardInactive : ''}`}
                                            >
                                                {/* Card Header */}
                                                <div className={styles.cusCardHeader}>
                                                    {/* Icon badge */}
                                                    <div
                                                        className={styles.stdIconBadge}
                                                        style={{
                                                            background: cat.colorHex ? `${cat.colorHex}1a` : '#f3f4f6',
                                                            border: `1px solid ${cat.colorHex ?? '#e5e7eb'}33`
                                                        }}
                                                    >
                                                        <CategoryIcon slug={cat.iconSlug} size={15} color={cat.colorHex ?? '#6b7280'} />
                                                    </div>

                                                    {/* Label + badges */}
                                                    <div className={styles.cusCardMeta}>
                                                        {renameDraft.target === 'category' && renameDraft.storeCategoryIdentifier === cat.storeCategoryIdentifier ? (
                                                            <div className={styles.renameInlineWrap}>
                                                                <input
                                                                    value={renameDraft.value}
                                                                    onChange={(e) => setRenameDraft(prev => ({ ...prev, value: e.target.value }))}
                                                                    className={styles.renameInlineInput}
                                                                    maxLength={80}
                                                                    autoFocus
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter') { e.preventDefault(); handleRenameSave(); }
                                                                        if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
                                                                    }}
                                                                />
                                                                <button
                                                                    className={styles.renameInlineBtn}
                                                                    onClick={handleRenameSave}
                                                                    disabled={renameLoading}
                                                                    title="Save"
                                                                >
                                                                    <Check size={13} strokeWidth={2.3} />
                                                                </button>
                                                                <button
                                                                    className={styles.renameInlineBtn}
                                                                    onClick={cancelRename}
                                                                    disabled={renameLoading}
                                                                    title="Cancel"
                                                                >
                                                                    <X size={13} strokeWidth={2.3} />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <span className={styles.stdCatLabel}>{cat.categoryLabel}</span>
                                                        )}
                                                        <div className={styles.stdBadgeRow}>
                                                            {cat.categorySource === 'adopted' && (
                                                                <span className={`${styles.stdBadge} ${styles.cusBadgeAdopted}`}>adopted</span>
                                                            )}
                                                            {cat.expenseType && (
                                                                <span className={`${styles.stdBadge} ${styles.stdBadgeType}`}>{cat.expenseType.toUpperCase()}</span>
                                                            )}
                                                            {cat.functionalGroup && (
                                                                <span className={`${styles.stdBadge} ${styles.stdBadgeGroup}`}>{cat.functionalGroup}</span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Actions: expand + active toggle */}
                                                    <div className={styles.cusCardActions}>
                                                        <button
                                                            className={styles.iconBtn}
                                                            onClick={() => startRenameCategory(cat)}
                                                            title="Rename category"
                                                            disabled={renameLoading}
                                                        >
                                                            <Pencil size={13} strokeWidth={2} />
                                                        </button>
                                                        {subcats.length > 0 && (
                                                            <button
                                                                className={styles.expandBtn}
                                                                onClick={() => setExpandedCustom(prev => ({
                                                                    ...prev,
                                                                    [cat.storeCategoryIdentifier]: !isExp
                                                                }))}
                                                                title={isExp ? 'Collapse' : 'Expand subcategories'}
                                                            >
                                                                {isExp
                                                                    ? <ChevronDown size={13} strokeWidth={2} />
                                                                    : <ChevronRight size={13} strokeWidth={2} />
                                                                }
                                                                <span>{subcats.length}</span>
                                                            </button>
                                                        )}
                                                        <button
                                                            className={`${styles.toggleBtn} ${isCatActive ? styles.toggleActive : styles.toggleInactive}`}
                                                            onClick={() => handleToggleCategory(cat)}
                                                            disabled={isCatToggling}
                                                            title={isCatActive ? 'Deactivate category' : 'Activate category'}
                                                        >
                                                            {isCatToggling ? '···' : isCatActive ? 'Active' : 'Inactive'}
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Subcategories accordion */}
                                                {isExp && subcats.length > 0 && (
                                                    <div className={styles.subcatList}>
                                                        {[...subcats]
                                                            .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder))
                                                            .map(sub => {
                                                                const isSubActive = sub.isActive === '1' || sub.isActive === 1;
                                                                const isSubToggling = toggleLoading.has(sub.storeSubcategoryIdentifier);
                                                                return (
                                                                    <div
                                                                        key={sub.storeSubcategoryIdentifier}
                                                                        className={`${styles.subcatRow} ${!isSubActive ? styles.subcatRowInactive : ''}`}
                                                                    >
                                                                        <div className={styles.subcatDot} style={{ background: cat.colorHex ?? '#d1d5db' }} />
                                                                        <div className={styles.subcatBody}>
                                                                            {renameDraft.target === 'subcategory'
                                                                                && renameDraft.storeSubcategoryIdentifier === sub.storeSubcategoryIdentifier
                                                                                ? (
                                                                                    <div className={styles.renameInlineWrap}>
                                                                                        <input
                                                                                            value={renameDraft.value}
                                                                                            onChange={(e) => setRenameDraft(prev => ({ ...prev, value: e.target.value }))}
                                                                                            className={styles.renameInlineInput}
                                                                                            maxLength={120}
                                                                                            autoFocus
                                                                                            onKeyDown={(e) => {
                                                                                                if (e.key === 'Enter') { e.preventDefault(); handleRenameSave(); }
                                                                                                if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
                                                                                            }}
                                                                                        />
                                                                                        <button
                                                                                            className={styles.renameInlineBtn}
                                                                                            onClick={handleRenameSave}
                                                                                            disabled={renameLoading}
                                                                                            title="Save"
                                                                                        >
                                                                                            <Check size={12} strokeWidth={2.3} />
                                                                                        </button>
                                                                                        <button
                                                                                            className={styles.renameInlineBtn}
                                                                                            onClick={cancelRename}
                                                                                            disabled={renameLoading}
                                                                                            title="Cancel"
                                                                                        >
                                                                                            <X size={12} strokeWidth={2.3} />
                                                                                        </button>
                                                                                    </div>
                                                                                )
                                                                                : <span className={styles.subcatLabel}>{sub.subcategoryLabel}</span>
                                                                            }
                                                                        </div>
                                                                        {sub.isGstItcEligible === '1' && (
                                                                            <span className={`${styles.stdBadge} ${styles.stdBadgeGst} ${styles.stdBadgeSm}`}>ITC</span>
                                                                        )}
                                                                        <button
                                                                            className={styles.iconBtn}
                                                                            onClick={() => startRenameSubcategory(cat, sub)}
                                                                            title="Rename subcategory"
                                                                            disabled={renameLoading}
                                                                        >
                                                                            <Pencil size={12} strokeWidth={2} />
                                                                        </button>
                                                                        <button
                                                                            className={`${styles.subcatToggleBtn} ${isSubActive ? styles.toggleActive : styles.toggleInactive}`}
                                                                            onClick={() => handleToggleSubcat(cat, sub)}
                                                                            disabled={isSubToggling}
                                                                            title={isSubActive ? 'Deactivate' : 'Activate'}
                                                                        >
                                                                            {isSubToggling ? '···' : isSubActive ? 'On' : 'Off'}
                                                                        </button>
                                                                    </div>
                                                                );
                                                            })
                                                        }
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* RIGHT: Standard Categories (Import) */}
                            <div className={styles.panel}>
                                {/* Same sticky-head handoff as the left panel — this one takes
                                    over once the user scrolls past "Your Categories" entirely. */}
                                <div className={styles.stickyPanelHead}>
                                    <div className={styles.panelHeader}>
                                        <div className={styles.panelTitleStack}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <span className={styles.panelTitle}>Standard Categories</span>
                                                <span className={styles.panelCount}>{notImportedStandard.length}</span>
                                            </div>
                                            <span className={styles.panelSubtitle}>Lokaci-curated categories for salon businesses.</span>
                                        </div>
                                        <div className={styles.stdHeaderRight}>
                                            {selectedCats.size > 0 && (
                                                <span className={styles.importFooterInfo}>
                                                    <span className={styles.importFooterCount}>{selectedCats.size}</span>
                                                    {' '}sel.
                                                    {selectedSubcats.size > 0 && (
                                                        <span className={styles.importFooterSubCount}>&nbsp;· {selectedSubcats.size} sub</span>
                                                    )}
                                                </span>
                                            )}
                                            <button
                                                className={styles.importSelectedBtn}
                                                onClick={handleImportSelected}
                                                disabled={importLoading || selectedCats.size === 0}
                                            >
                                                {importLoading ? 'Importing...' : 'Import Selected'}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Standard search bar */}
                                    <div className={styles.stdSearchRow}>
                                        <input
                                            type="text"
                                            placeholder="Search categories or subcategories..."
                                            value={stdSearchQuery}
                                            onChange={e => setStdSearchQuery(e.target.value)}
                                            className={styles.searchInput}
                                        />
                                        {selectedCats.size > 0 && (
                                            <button
                                                className={styles.clearSelBtn}
                                                onClick={() => { setSelectedCats(new Set()); setSelectedSubcats(new Set()); }}
                                                title="Clear selection"
                                            >
                                                Clear
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className={styles.categoryList}>
                                    {filteredStandard.length === 0 && (
                                        <div className={styles.emptyState}>
                                            {stdSearchQuery ? 'No categories match your search.' : 'All standard categories have been imported.'}
                                        </div>
                                    )}
                                    {filteredStandard.map((cat) => {
                                        const isExpanded = !!expandedStandard[cat.standardCategoryIdentifier];
                                        const subcats = Array.isArray(cat.subcategories) ? cat.subcategories : [];
                                        const isCatSelected = selectedCats.has(cat.standardCategoryIdentifier);
                                        // indeterminate: some but not all subcats selected
                                        const selectedSubCount = subcats.filter(s => selectedSubcats.has(s.standardSubcategoryIdentifier)).length;
                                        const isIndeterminate = !isCatSelected && selectedSubCount > 0;
                                        return (
                                            <div
                                                key={cat.standardCategoryIdentifier}
                                                className={`${styles.stdCard} ${isCatSelected ? styles.stdCardSelected : ''}`}
                                            >
                                                {/* Card Header */}
                                                <div className={styles.stdCardHeader}>
                                                    {/* Lucide checkbox toggle */}
                                                    <button
                                                        className={`${styles.stdCheckToggle} ${isCatSelected ? styles.stdCheckToggleOn : isIndeterminate ? styles.stdCheckToggleMid : ''}`}
                                                        onClick={() => toggleCat(cat.standardCategoryIdentifier, subcats)}
                                                        title={isCatSelected ? 'Deselect' : 'Select'}
                                                    >
                                                        {isCatSelected
                                                            ? <CheckSquare2 size={17} strokeWidth={2} />
                                                            : isIndeterminate
                                                                ? <Minus size={17} strokeWidth={2.5} />
                                                                : <Square size={17} strokeWidth={1.6} />
                                                        }
                                                    </button>
                                                    <div className={styles.stdCardLeft}>
                                                        {/* Color icon badge */}
                                                        <div
                                                            className={styles.stdIconBadge}
                                                            style={{ background: cat.colorHex ? `${cat.colorHex}1a` : '#f3f4f6', border: `1px solid ${cat.colorHex ?? '#e5e7eb'}33` }}
                                                        >
                                                            <CategoryIcon slug={cat.iconSlug} size={15} color={cat.colorHex ?? '#6b7280'} />
                                                        </div>
                                                        <div className={styles.stdCardMeta}>
                                                            <span className={styles.stdCatLabel}>{cat.standardCategoryLabel}</span>
                                                            <div className={styles.stdBadgeRow}>
                                                                {cat.expenseType && (
                                                                    <span className={`${styles.stdBadge} ${styles.stdBadgeType}`}>{cat.expenseType.toUpperCase()}</span>
                                                                )}
                                                                {cat.functionalGroup && (
                                                                    <span className={`${styles.stdBadge} ${styles.stdBadgeGroup}`}>{cat.functionalGroup}</span>
                                                                )}
                                                                {cat.isGstItcEligible === '1' && (
                                                                    <span className={`${styles.stdBadge} ${styles.stdBadgeGst}`}>GST ITC</span>
                                                                )}
                                                                {cat.isRecurring === '1' && (
                                                                    <span className={`${styles.stdBadge} ${styles.stdBadgeRecurring}`}>Recurring</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className={styles.stdCardActions}>
                                                        {subcats.length > 0 && (
                                                            <button
                                                                className={styles.expandBtn}
                                                                onClick={() => setExpandedStandard(prev => ({
                                                                    ...prev,
                                                                    [cat.standardCategoryIdentifier]: !isExpanded
                                                                }))}
                                                                title={isExpanded ? 'Collapse' : 'Expand subcategories'}
                                                            >
                                                                {isExpanded
                                                                    ? <ChevronDown size={13} strokeWidth={2} />
                                                                    : <ChevronRight size={13} strokeWidth={2} />
                                                                }
                                                                <span>{selectedSubCount > 0 ? `${selectedSubCount}/` : ''}{subcats.length}</span>
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Description */}
                                                {cat.standardCategoryDescription && (
                                                    <p className={styles.stdCatDesc}>{cat.standardCategoryDescription}</p>
                                                )}

                                                {/* Subcategories */}
                                                {isExpanded && subcats.length > 0 && (
                                                    <div className={styles.subcatList}>
                                                        {[...subcats]
                                                            .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder))
                                                            .map(sub => {
                                                                const isSubSelected = selectedSubcats.has(sub.standardSubcategoryIdentifier);
                                                                return (
                                                                    <div
                                                                        key={sub.standardSubcategoryIdentifier}
                                                                        className={`${styles.subcatRow} ${isSubSelected ? styles.subcatRowSelected : ''}`}
                                                                    >
                                                                        <button
                                                                            className={`${styles.stdCheckToggle} ${isSubSelected ? styles.stdCheckToggleOn : ''}`}
                                                                            onClick={() => toggleSubcat(cat.standardCategoryIdentifier, sub.standardSubcategoryIdentifier, subcats)}
                                                                            title={isSubSelected ? 'Deselect' : 'Select'}
                                                                        >
                                                                            {isSubSelected
                                                                                ? <CheckSquare2 size={14} strokeWidth={2} />
                                                                                : <Square size={14} strokeWidth={1.6} />
                                                                            }
                                                                        </button>
                                                                        <div className={styles.subcatDot} style={{ background: cat.colorHex ?? '#d1d5db' }} />
                                                                        <div className={styles.subcatBody}>
                                                                            <span className={styles.subcatLabel}>{sub.standardSubcategoryLabel}</span>
                                                                            {sub.helperNote && (
                                                                                <span className={styles.subcatNote}>{sub.helperNote}</span>
                                                                            )}
                                                                        </div>
                                                                        {sub.isGstItcEligible === '1' && (
                                                                            <span className={`${styles.stdBadge} ${styles.stdBadgeGst} ${styles.stdBadgeSm}`}>ITC</span>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })
                                                        }
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                        </div>
                    </div>
                )}

            </div>

            {/* ── Add Category / Subcategory Drawer ────────────────────────── */}
            <AnimatePresence>
                {isAdding && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            className={styles.drawerBackdrop}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.22 }}
                            onClick={() => { setIsAdding(false); setAddForm(EMPTY_ADD_FORM); setAddErrors({}); }}
                        />

                        {/* Panel */}
                        <motion.div
                            className={styles.drawerPanel}
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', stiffness: 340, damping: 34 }}
                        >
                            {/* Drawer header */}
                            <div className={styles.drawerHeader}>
                                <div className={styles.drawerHeaderLeft}>
                                    <div className={styles.drawerHeaderIcon}>
                                        <CategoryIcon
                                            slug={addForm.target === 'category' ? 'Tag' : 'Package'}
                                            size={16}
                                            color="#fff"
                                        />
                                    </div>
                                    <div>
                                        <span className={styles.drawerTitle}>
                                            {addForm.target === 'category' ? 'New Category' : 'New Subcategory'}
                                        </span>
                                        <span className={styles.drawerSubtitle}>
                                            {addForm.target === 'category'
                                                ? 'Create a custom expense category for your store'
                                                : 'Add a subcategory under an existing category'}
                                        </span>
                                    </div>
                                </div>
                                <button
                                    className={styles.drawerCloseBtn}
                                    onClick={() => { setIsAdding(false); setAddForm(EMPTY_ADD_FORM); setAddErrors({}); }}
                                    aria-label="Close"
                                >
                                    ✕
                                </button>
                            </div>

                            {/* Target toggle — Category | Subcategory */}
                            <div className={styles.drawerTargetRow}>
                                {['category', 'subcategory'].map(t => (
                                    <button
                                        key={t}
                                        type="button"
                                        className={`${styles.drawerTargetBtn} ${addForm.target === t ? styles.drawerTargetBtnActive : ''}`}
                                        onClick={() => { setAddField('target', t); setAddErrors({}); }}
                                    >
                                        <CategoryIcon
                                            slug={t === 'category' ? 'Tag' : 'Package'}
                                            size={13}
                                            color={addForm.target === t ? '#fff' : '#7c3aed'}
                                        />
                                        {t === 'category' ? 'Category' : 'Subcategory'}
                                    </button>
                                ))}
                            </div>

                            {/* Scrollable body */}
                            <form
                                onSubmit={handleAddCategory}
                                className={styles.drawerBody}
                                noValidate
                            >
                                {addForm.target === 'category' ? (
                                    <>
                                        {/* Category Label */}
                                        <div className={styles.drawerField}>
                                            <label className={styles.drawerLabel}>Category name <span className={styles.req}>*</span></label>
                                            <input
                                                type="text"
                                                value={addForm.categoryLabel}
                                                onChange={e => setAddField('categoryLabel', e.target.value)}
                                                placeholder="e.g. Franchise Costs"
                                                className={`${styles.drawerInput} ${addErrors.categoryLabel ? styles.drawerInputError : ''}`}
                                                autoFocus
                                                maxLength={80}
                                            />
                                            {addErrors.categoryLabel
                                                ? <span className={styles.fieldError}>{addErrors.categoryLabel}</span>
                                                : <span className={styles.drawerHint}>Tip: keep it short and recognisable · max 80 chars</span>
                                            }
                                        </div>

                                        {/* Expense Type */}
                                        <div className={styles.drawerField}>
                                            <label className={styles.drawerLabel}>Expense type <span className={styles.req}>*</span></label>
                                            <div className={styles.drawerSegmented}>
                                                {[{v:'opex',l:'OPEX',d:'Day-to-day'},{v:'capex',l:'CAPEX',d:'Long-term assets'},{v:'mixed',l:'Mixed',d:'Both'}].map(({v,l,d}) => (
                                                    <button
                                                        key={v}
                                                        type="button"
                                                        className={`${styles.drawerSegBtn} ${addForm.expenseType === v ? styles.drawerSegBtnActive : ''}`}
                                                        onClick={() => setAddField('expenseType', v)}
                                                    >
                                                        <span className={styles.drawerSegBtnLabel}>{l}</span>
                                                        <span className={styles.drawerSegBtnDesc}>{d}</span>
                                                    </button>
                                                ))}
                                            </div>
                                            {addErrors.expenseType && <span className={styles.fieldError}>{addErrors.expenseType}</span>}
                                        </div>

                                        {/* Functional Group */}
                                        <div className={styles.drawerField}>
                                            <label className={styles.drawerLabel}>Functional group <span className={styles.req}>*</span></label>
                                            <div className={styles.drawerChips}>
                                                {[
                                                    {v:'service', l:'Service', icon:'Scissors'},
                                                    {v:'people',  l:'People',  icon:'Users'},
                                                    {v:'infra',   l:'Infrastructure', icon:'Home'},
                                                    {v:'compliance', l:'Compliance', icon:'ShieldCheck'},
                                                    {v:'other',   l:'Other',   icon:'MoreHorizontal'},
                                                ].map(({v,l,icon}) => (
                                                    <button
                                                        key={v}
                                                        type="button"
                                                        className={`${styles.drawerChip} ${addForm.functionalGroup === v ? styles.drawerChipActive : ''}`}
                                                        onClick={() => setAddField('functionalGroup', v)}
                                                    >
                                                        <CategoryIcon slug={icon} size={12} color={addForm.functionalGroup === v ? '#fff' : '#6d28d9'} />
                                                        {l}
                                                    </button>
                                                ))}
                                            </div>
                                            {addErrors.functionalGroup && <span className={styles.fieldError}>{addErrors.functionalGroup}</span>}
                                        </div>

                                        {/* Colour */}
                                        <div className={styles.drawerField}>
                                            <label className={styles.drawerLabel}>Colour</label>
                                            <div className={styles.drawerColorRow}>
                                                <input
                                                    type="color"
                                                    value={addForm.colorHex}
                                                    onChange={e => setAddField('colorHex', e.target.value)}
                                                    className={styles.drawerColorSwatch}
                                                    title="Pick a colour"
                                                />
                                                <input
                                                    type="text"
                                                    value={addForm.colorHex}
                                                    onChange={e => setAddField('colorHex', e.target.value)}
                                                    placeholder="#888780"
                                                    className={`${styles.drawerInput} ${styles.drawerColorHexInput} ${addErrors.colorHex ? styles.drawerInputError : ''}`}
                                                    maxLength={7}
                                                />
                                                {/* Preset swatches */}
                                                <div className={styles.drawerSwatchPresets}>
                                                    {['#6d28d9','#0ea5e9','#10b981','#f59e0b','#ef4444','#ec4899','#8b5cf6','#64748b'].map(hex => (
                                                        <button
                                                            key={hex}
                                                            type="button"
                                                            className={`${styles.drawerSwatchDot} ${addForm.colorHex === hex ? styles.drawerSwatchDotActive : ''}`}
                                                            style={{ background: hex }}
                                                            onClick={() => setAddField('colorHex', hex)}
                                                            title={hex}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                            {addErrors.colorHex && <span className={styles.fieldError}>{addErrors.colorHex}</span>}
                                        </div>

                                        {/* Icon */}
                                        <div className={styles.drawerField}>
                                            <label className={styles.drawerLabel}>Icon</label>
                                            <div className={styles.drawerIconGrid}>
                                                {Object.keys(LUCIDE_ICON_MAP)
                                                    .filter(k => !['CheckSquare2','Square','Minus'].includes(k))
                                                    .map(slug => (
                                                        <button
                                                            key={slug}
                                                            type="button"
                                                            className={`${styles.drawerIconOption} ${addForm.iconSlug === slug ? styles.drawerIconOptionActive : ''}`}
                                                            onClick={() => setAddField('iconSlug', slug)}
                                                            title={slug}
                                                        >
                                                            <CategoryIcon
                                                                slug={slug}
                                                                size={15}
                                                                color={addForm.iconSlug === slug ? addForm.colorHex : '#64748b'}
                                                            />
                                                        </button>
                                                    ))
                                                }
                                            </div>
                                        </div>

                                        {/* Sort order */}
                                        <div className={styles.drawerField}>
                                            <label className={styles.drawerLabel}>Sort order <span className={styles.drawerOptional}>(optional)</span></label>
                                            <input
                                                type="number"
                                                min="0"
                                                value={addForm.sortOrder}
                                                onChange={e => setAddField('sortOrder', e.target.value)}
                                                placeholder="e.g. 200"
                                                className={`${styles.drawerInput} ${styles.drawerInputSm} ${addErrors.sortOrder ? styles.drawerInputError : ''}`}
                                            />
                                            {addErrors.sortOrder
                                                ? <span className={styles.fieldError}>{addErrors.sortOrder}</span>
                                                : <span className={styles.drawerHint}>Lower numbers appear first in lists</span>
                                            }
                                        </div>

                                        {/* Live preview card */}
                                        {addForm.categoryLabel.trim() && (
                                            <div className={styles.drawerPreviewCard}>
                                                <span className={styles.drawerPreviewBadge}>Preview</span>
                                                <div className={styles.drawerPreviewInner}>
                                                    <div
                                                        className={styles.drawerPreviewIcon}
                                                        style={{
                                                            background: `${addForm.colorHex}20`,
                                                            border: `1.5px solid ${addForm.colorHex}40`,
                                                        }}
                                                    >
                                                        <CategoryIcon slug={addForm.iconSlug} size={18} color={addForm.colorHex} />
                                                    </div>
                                                    <div>
                                                        <span className={styles.drawerPreviewName}>{addForm.categoryLabel.trim()}</span>
                                                        <span className={styles.drawerPreviewMeta}>
                                                            {addForm.expenseType.toUpperCase()} · {addForm.functionalGroup}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {/* Parent category */}
                                        <div className={styles.drawerField}>
                                            <label className={styles.drawerLabel}>Parent category <span className={styles.req}>*</span></label>
                                            <select
                                                value={addForm.parentCategoryId}
                                                onChange={e => setAddField('parentCategoryId', e.target.value)}
                                                className={`${styles.drawerInput} ${styles.drawerSelect} ${addErrors.parentCategoryId ? styles.drawerInputError : ''}`}
                                            >
                                                <option value="">— select a category —</option>
                                                {[...customCategories]
                                                    .filter(c => c.isActive === 1)
                                                    .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder))
                                                    .map(c => (
                                                        <option key={c.storeCategoryIdentifier} value={c.storeCategoryIdentifier}>
                                                            {c.categoryLabel}
                                                        </option>
                                                    ))
                                                }
                                            </select>
                                            {addErrors.parentCategoryId && <span className={styles.fieldError}>{addErrors.parentCategoryId}</span>}
                                        </div>

                                        {/* Subcategory name */}
                                        <div className={styles.drawerField}>
                                            <label className={styles.drawerLabel}>Subcategory name <span className={styles.req}>*</span></label>
                                            <input
                                                type="text"
                                                value={addForm.subcategoryLabel}
                                                onChange={e => setAddField('subcategoryLabel', e.target.value)}
                                                placeholder="e.g. Henna & Mehendi Supplies"
                                                className={`${styles.drawerInput} ${addErrors.subcategoryLabel ? styles.drawerInputError : ''}`}
                                                autoFocus
                                                maxLength={120}
                                            />
                                            {addErrors.subcategoryLabel
                                                ? <span className={styles.fieldError}>{addErrors.subcategoryLabel}</span>
                                                : <span className={styles.drawerHint}>Max 120 characters</span>
                                            }
                                        </div>

                                        {/* GST ITC eligible */}
                                        <div className={styles.drawerField}>
                                            <label className={styles.drawerLabel}>GST ITC eligible</label>
                                            <div className={styles.drawerTogglePill}>
                                                {[{v:1,l:'Yes — eligible for input tax credit'},{v:0,l:'No — not eligible'}].map(({v,l}) => (
                                                    <button
                                                        key={v}
                                                        type="button"
                                                        className={`${styles.drawerToggleOpt} ${addForm.isGstItcEligible === v ? styles.drawerToggleOptActive : ''}`}
                                                        onClick={() => setAddField('isGstItcEligible', v)}
                                                    >
                                                        <span className={styles.drawerToggleDot} />
                                                        {l}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Expense type override */}
                                        <div className={styles.drawerField}>
                                            <label className={styles.drawerLabel}>Expense type override <span className={styles.drawerOptional}>(optional)</span></label>
                                            <select
                                                value={addForm.expenseTypeOverride ?? ''}
                                                onChange={e => setAddField('expenseTypeOverride', e.target.value || null)}
                                                className={`${styles.drawerInput} ${styles.drawerSelect}`}
                                            >
                                                <option value="">Inherit from parent category</option>
                                                <option value="opex">OPEX — Operating</option>
                                                <option value="capex">CAPEX — Capital</option>
                                                <option value="mixed">Mixed</option>
                                            </select>
                                            <span className={styles.drawerHint}>Leave blank to inherit the parent category's expense type</span>
                                        </div>

                                        {/* Sort order */}
                                        <div className={styles.drawerField}>
                                            <label className={styles.drawerLabel}>Sort order <span className={styles.drawerOptional}>(optional)</span></label>
                                            <input
                                                type="number"
                                                min="0"
                                                value={addForm.sortOrder}
                                                onChange={e => setAddField('sortOrder', e.target.value)}
                                                placeholder="e.g. 130"
                                                className={`${styles.drawerInput} ${styles.drawerInputSm} ${addErrors.sortOrder ? styles.drawerInputError : ''}`}
                                            />
                                            {addErrors.sortOrder
                                                ? <span className={styles.fieldError}>{addErrors.sortOrder}</span>
                                                : <span className={styles.drawerHint}>Lower numbers appear first</span>
                                            }
                                        </div>
                                    </>
                                )}

                                {/* Spacer so footer doesn't overlap last field */}
                                <div style={{ height: '5rem' }} />
                            </form>

                            {/* Sticky footer */}
                            <div className={styles.drawerFooter}>
                                <button
                                    type="button"
                                    className={styles.drawerCancelBtn}
                                    onClick={() => { setIsAdding(false); setAddForm(EMPTY_ADD_FORM); setAddErrors({}); }}
                                    disabled={addLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className={styles.drawerSubmitBtn}
                                    onClick={handleAddCategory}
                                    disabled={addLoading}
                                >
                                    {addLoading
                                        ? <><span className={styles.drawerSpinner} /> Creating…</>
                                        : addForm.target === 'category' ? 'Create Category' : 'Add Subcategory'
                                    }
                                </button>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

        </div>
    );
};

export default ExpenseSetting;